import { usePortfolioData } from '@/hooks/use-portfolio-data';
import { Skeleton } from '@/components/ui/skeleton';
import * as Icons from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

export function Skills() {
  const { data: portfolioData, isLoading } = usePortfolioData();

  const DynamicIcon = ({ name }: { name: string }) => {
    const LucideIcon = (Icons as any)[name] || Icons.Circle;
    return <LucideIcon className="text-primary h-6 w-6" />;
  };

  if (isLoading) {
    return (
      <section id="skills" className="py-24 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center mb-16">
            <Skeleton className="h-8 w-40 rounded-full mb-4" />
            <Skeleton className="h-10 w-96 mx-auto mb-4" />
            <Skeleton className="w-20 h-1.5 mx-auto mb-4" />
            <Skeleton className="h-5 w-96 mx-auto" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-96 rounded-2xl" />
            ))}
          </div>
          
          <Skeleton className="h-60 rounded-2xl" />
        </div>
      </section>
    );
  }

  if (!portfolioData) return null;

  return (
    <section id="skills" className="py-24 relative">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/20 to-background"></div>
      <div className="absolute left-0 top-1/4 w-72 h-72 bg-primary/5 rounded-full filter blur-3xl"></div>
      <div className="absolute right-0 bottom-1/4 w-72 h-72 bg-secondary/5 rounded-full filter blur-3xl"></div>
      
      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.01] dark:opacity-[0.03]" 
        style={{ 
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
        }}
      ></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col items-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4 animate-fade-in">
            <span className="animate-pulse mr-2">✦</span> Skills & Expertise
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center tracking-tight animate-slide-up">
            Professional <span className="bg-gradient-text">Skillset</span>
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-primary to-secondary rounded-full mb-6 animate-slide-up animate-delay-100"></div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto text-center animate-slide-up animate-delay-200">
            Here are the technologies and tools I work with on a daily basis to create powerful, efficient, and beautiful applications.
          </p>
        </div>
        
        {/* Skill Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {portfolioData.skillCategories.map((category, index) => (
            <Card 
              key={category.name} 
              className="card-hover border-muted/30 rounded-2xl overflow-hidden"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <CardHeader className="pb-0 pt-6">
                <div className="flex items-center mb-4">
                  <div className="rounded-xl bg-primary/10 p-3 mr-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                    <DynamicIcon name={category.icon} />
                  </div>
                  <h3 className="text-2xl font-bold">{category.name}</h3>
                </div>
                <p className="text-muted-foreground">{category.description}</p>
              </CardHeader>
              
              <CardContent className="pt-6">
                <ul className="space-y-4">
                  {category.skills.map((skill, idx) => (
                    <li key={skill.name} className="group"
                        style={{ animation: 'slideUp 0.5s ease-out forwards', animationDelay: `${idx * 100 + 300}ms` }}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium group-hover:text-primary transition-colors">{skill.name}</span>
                        <Badge variant="outline" className="bg-background font-semibold hover:bg-primary hover:text-white transition-colors">
                          {skill.level}%
                        </Badge>
                      </div>
                      <Progress 
                        value={skill.level} 
                        className="h-2.5 bg-muted"
                        style={{
                          '--progress-background': 'linear-gradient(to right, hsl(var(--primary)), hsl(var(--secondary)))'
                        } as React.CSSProperties}
                      />
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* All Skills Tags */}
        <Card className="rounded-2xl border-muted/30 card-hover shadow-lg overflow-hidden animate-fade-in animate-delay-500">
          <CardHeader>
            <h3 className="text-2xl font-bold text-center">Technologies & Tools</h3>
          </CardHeader>
          <CardContent className="flex flex-wrap justify-center gap-3 py-6">
            {portfolioData.allSkills.map((skill, index) => (
              <Badge 
                key={skill}
                variant="secondary"
                className={cn(
                  "px-4 py-2 text-sm font-medium hover:bg-primary hover:text-white transition-all duration-300 cursor-default",
                  "transform hover:-translate-y-1 hover:shadow-md"
                )}
                style={{ 
                  animation: 'fadeIn 0.5s ease-out forwards',
                  animationDelay: `${index * 50 + 500}ms`
                }}
              >
                {skill}
              </Badge>
            ))}
          </CardContent>
        </Card>
        
        {/* Animated decorative element */}
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-full h-4 overflow-hidden z-0">
          <div className="w-full h-full bg-gradient-to-r from-transparent via-primary/10 to-transparent animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}

export default Skills;
